// EmailDraftPanel — "Write with AI" for the compose window. On demand only:
// nothing is generated until the user picks an angle and clicks Generate.
//
// Unlike EmailAssistPanel (bullet POINTS, hard-requires a vendor SOP), this
// streams a complete draft the user can drop straight into the editor, then
// edit and send. It works for replies (thread as context), for brand-new mail
// (their instruction as context), and for forwards.
//
// SSE: POST returning text/event-stream, so we use the fetch + getReader +
// `data: ` line pattern the rest of the codebase uses for streaming AI.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select as AntSelect } from "antd";
import {
  Sparkles, RefreshCw, Copy, Check, AlertCircle, CornerDownLeft, Plus, Square,
} from "lucide-react";
import toast from "react-hot-toast";
import { SERVER_URL } from "../../../utils/serverConfig";
import { splitDraftSubject } from "../utils/emailComposeUtils";

// Angles the user can pick. Keys are the API contract (server-side INTENTS).
// `threadOnly` entries only make sense when there is a conversation to react to.
const INTENTS = [
  { key: "reply",    label: "Reply",           threadOnly: true },
  { key: "positive", label: "Say yes",         threadOnly: true },
  { key: "decline",  label: "Politely decline", threadOnly: true },
  { key: "info",     label: "Ask for details" },
  { key: "meeting",  label: "Propose a meeting" },
  { key: "followup", label: "Follow up" },
  { key: "thanks",   label: "Thank / confirm",  threadOnly: true },
  { key: "intro",    label: "First outreach",   newOnly: true },
];

// `hint` is what the model is actually told, paraphrased — it shows under each
// option in the dropdown so the choice isn't guesswork.
const TONES = [
  { key: "professional", label: "Professional", hint: "Clear and businesslike" },
  { key: "friendly",     label: "Friendly",     hint: "Warm, conversational" },
  { key: "direct",       label: "Direct",       hint: "Straight to the point" },
  { key: "formal",       label: "Formal",       hint: "Precise, no contractions" },
  { key: "enthusiastic", label: "Enthusiastic", hint: "Positive, never salesy" },
];

const LENGTHS = [
  { key: "short",    label: "Short",    hint: "50–90 words" },
  { key: "medium",   label: "Medium",   hint: "110–170 words" },
  { key: "detailed", label: "Detailed", hint: "200–300 words" },
];

// Shared render for both dropdowns: bold label with the hint underneath.
const renderOption = (opt) => (
  <div style={{ lineHeight: 1.35, padding: "2px 0" }}>
    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--color-text-primary)" }}>{opt.label}</div>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{opt.data?.hint}</div>
  </div>
);

const toOptions = (list) => list.map((o) => ({ value: o.key, label: o.label, hint: o.hint }));

// The compose Modal sits at z-1100 and antd popups default to 1050, so without
// this the dropdown opens *behind* the modal. 1300 clears the modal and the
// lead-file picker (1200).
const POPUP_STYLES = { popup: { root: { zIndex: 1300 } } };

const labelStyle = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--color-text-secondary)",
  marginBottom: 5,
  display: "block",
};

function Chip({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "5px 10px",
        fontSize: 11.5,
        fontWeight: active ? 700 : 500,
        borderRadius: 999,
        border: `1px solid ${active ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
        background: active ? "var(--crm-primary)" : "var(--color-bg-surface)",
        color: active ? "#fff" : "var(--color-text-secondary)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "all 120ms",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

export default function EmailDraftPanel({
  accountId,
  threadId,
  leadId,
  mode = "new",
  subject = "",
  recipients = [],
  bodyHasContent = false,
  onApply,
}) {
  const hasThread = !!threadId;
  const [intent, setIntent] = useState(hasThread ? "reply" : "intro");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [instruction, setInstruction] = useState("");

  const [text, setText] = useState("");
  const [status, setStatus] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // Abort any in-flight stream when the panel unmounts (drawer closed, modal
  // closed) so we don't keep the Kimi call — and the Gmail fetch — alive.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Keep the newest tokens in view while streaming.
  useEffect(() => {
    if (streaming && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [text, streaming]);

  const intents = useMemo(
    () => INTENTS.filter((i) => (hasThread ? !i.newOnly : !i.threadOnly)),
    [hasThread],
  );

  // Reset the angle if the context flips (e.g. modal reused for a new message).
  useEffect(() => {
    if (!intents.some((i) => i.key === intent)) setIntent(intents[0]?.key || "reply");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasThread]);

  // Brand-new mail with no thread and no lead has nothing to write from — the
  // server would 422, so we ask for the instruction up front instead.
  const needsInstruction = !hasThread && !leadId;
  const canGenerate = !streaming && (!needsInstruction || instruction.trim().length > 0);

  // A brand-new email with an empty subject gets a subject line suggested too.
  const wantSubject = mode === "new" && !String(subject || "").trim();

  const { subject: draftSubject, body: draftBody } = useMemo(
    () => splitDraftSubject(text),
    [text],
  );

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  };

  const run = async () => {
    if (!canGenerate) return;
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setStreaming(true);
    setError(null);
    setText("");
    setStatus("");
    setCopied(false);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${SERVER_URL}/api/v1/crm/email-assist/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          accountId: accountId || undefined,
          threadId: threadId || undefined,
          leadId: leadId || undefined,
          intent,
          tone,
          length,
          instruction: instruction.trim() || undefined,
          wantSubject,
          subject: subject || undefined,
          recipients: recipients.length ? recipients : undefined,
        }),
        signal: abortRef.current.signal,
      });

      if (!res.ok || !res.body) throw new Error(`Request failed (${res.status})`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n");
        buffer = parts.pop() || "";
        for (const line of parts) {
          if (!line.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === "status") setStatus(d.message || "");
            else if (d.type === "content") setText((prev) => prev + (d.text || ""));
            else if (d.type === "error") setError({ message: d.message || "Something went wrong", code: d.code });
          } catch (_) {
            /* ignore malformed SSE line */
          }
        }
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        setError({ message: err?.message || "Failed to generate a draft" });
      }
    } finally {
      setStreaming(false);
    }
  };

  const copy = async () => {
    if (!draftBody) return;
    try {
      await navigator.clipboard.writeText(draftBody);
      setCopied(true);
      toast.success("Draft copied");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const apply = (append) => {
    if (!draftBody) return;
    onApply?.({ body: draftBody, subject: draftSubject, append });
  };

  const onInstructionKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      run();
    }
  };

  const hasContent = draftBody.trim().length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, height: "100%", minHeight: 0 }}>
      {/* Angle */}
      <div>
        <span style={labelStyle}>What should it say?</span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {intents.map((i) => (
            <Chip
              key={i.key}
              active={intent === i.key}
              disabled={streaming}
              onClick={() => setIntent(i.key)}
            >
              {i.label}
            </Chip>
          ))}
        </div>
      </div>

      {/* Tone + length */}
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={labelStyle}>Tone</span>
          <AntSelect
            value={tone}
            onChange={setTone}
            disabled={streaming}
            options={toOptions(TONES)}
            optionRender={renderOption}
            popupMatchSelectWidth={230}
            styles={POPUP_STYLES}
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={labelStyle}>Length</span>
          <AntSelect
            value={length}
            onChange={setLength}
            disabled={streaming}
            options={toOptions(LENGTHS)}
            optionRender={renderOption}
            popupMatchSelectWidth={200}
            styles={POPUP_STYLES}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {/* Optional steer */}
      <div>
        <span style={labelStyle}>
          Anything specific? {needsInstruction && <span style={{ color: "var(--crm-danger)" }}>(required)</span>}
        </span>
        <textarea
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={onInstructionKeyDown}
          disabled={streaming}
          rows={3}
          maxLength={1200}
          placeholder={hasThread
            ? "e.g. confirm the 20% discount, ask for their PO by Friday"
            : "e.g. introduce us, mention we ship from 3 warehouses, ask for a 15-min call"}
          style={{
            width: "100%",
            padding: "8px 10px",
            fontSize: 12.5,
            lineHeight: 1.5,
            borderRadius: 8,
            border: "1px solid var(--color-border-subtle)",
            background: "var(--color-bg-surface)",
            color: "var(--color-text-primary)",
            resize: "vertical",
            outline: "none",
            fontFamily: "inherit",
          }}
        />
      </div>

      {/* Generate / stop */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={run}
          disabled={!canGenerate}
          style={{
            flex: 1,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
            padding: "9px 12px", fontSize: 12.5, fontWeight: 700,
            borderRadius: 9, border: "none",
            color: "#fff",
            background: canGenerate
              ? "linear-gradient(135deg, #6366f1, #8b5cf6)"
              : "var(--color-bg-muted)",
            cursor: canGenerate ? "pointer" : "not-allowed",
            boxShadow: canGenerate ? "0 2px 8px rgba(99,102,241,0.25)" : "none",
            transition: "all 120ms",
          }}
        >
          {streaming
            ? <><RefreshCw size={13} className="animate-spin" /> Writing…</>
            : <><Sparkles size={13} /> {hasContent ? "Regenerate" : "Generate draft"}</>}
        </button>
        {streaming && (
          <button
            type="button"
            onClick={stop}
            title="Stop generating"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "9px 12px", fontSize: 12, fontWeight: 600,
              borderRadius: 9, border: "1px solid var(--color-border-subtle)",
              background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer",
            }}
          >
            <Square size={11} /> Stop
          </button>
        )}
      </div>

      {needsInstruction && !instruction.trim() && (
        <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", lineHeight: 1.5, marginTop: -6 }}>
          There's no thread or linked lead to read, so tell the AI what this email should say.
        </div>
      )}

      {error && !streaming && (
        <div style={{
          background: "var(--crm-danger-soft)", color: "var(--crm-danger)",
          borderRadius: 9, padding: 10,
          display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, lineHeight: 1.5,
        }}>
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{error.message}</div>
        </div>
      )}

      {/* Draft preview */}
      {(hasContent || streaming) && (
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
          {draftSubject && (
            <div style={{
              fontSize: 12, marginBottom: 6, color: "var(--color-text-primary)",
              background: "var(--crm-primary-soft)", borderRadius: 7, padding: "6px 9px",
            }}>
              <strong style={{ color: "var(--crm-primary)" }}>Subject:</strong> {draftSubject}
            </div>
          )}
          <div
            ref={scrollRef}
            style={{
              flex: 1, minHeight: 120, maxHeight: 340, overflowY: "auto",
              padding: 12,
              borderRadius: 10,
              border: "1px solid var(--color-border-subtle)",
              background: "var(--color-bg-surface)",
              fontSize: 13, lineHeight: 1.65,
              color: "var(--color-text-primary)",
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}
          >
            {draftBody}
            {streaming && (
              <span
                aria-hidden
                style={{
                  display: "inline-block", width: 7, height: 14,
                  background: "var(--crm-primary)", borderRadius: 2,
                  marginLeft: 2, verticalAlign: "text-bottom",
                  animation: "crmDraftBlink 1s steps(2) infinite",
                }}
              />
            )}
            {streaming && !draftBody && (
              <span style={{ color: "var(--color-text-secondary)", fontSize: 12.5 }}>
                {status || "Thinking…"}
              </span>
            )}
            <style>{`@keyframes crmDraftBlink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
          </div>
        </div>
      )}

      {/* Apply */}
      {hasContent && !streaming && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button
            type="button"
            onClick={() => apply(false)}
            style={{
              flex: 1, minWidth: 150,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "9px 12px", fontSize: 12.5, fontWeight: 700,
              borderRadius: 9, border: "none",
              background: "var(--crm-primary)", color: "#fff", cursor: "pointer",
              boxShadow: "0 2px 6px rgba(99,102,241,0.25)",
            }}
          >
            <CornerDownLeft size={13} />
            {bodyHasContent ? "Replace email body" : "Use this draft"}
          </button>
          {bodyHasContent && (
            <button
              type="button"
              onClick={() => apply(true)}
              title="Add below what you've already written"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "9px 12px", fontSize: 12, fontWeight: 600,
                borderRadius: 9, border: "1px solid var(--color-border-subtle)",
                background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer",
              }}
            >
              <Plus size={12} /> Append
            </button>
          )}
          <button
            type="button"
            onClick={copy}
            title="Copy draft"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "9px 12px", fontSize: 12, fontWeight: 600,
              borderRadius: 9, border: "1px solid var(--color-border-subtle)",
              background: "transparent",
              color: copied ? "var(--crm-success)" : "var(--color-text-secondary)",
              cursor: "pointer",
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}

      {hasContent && !streaming && (
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
          AI-written — read it before sending, and fill in anything in [square brackets].
        </div>
      )}
    </div>
  );
}
